// Singleton in-memory cache of the SSDC employee directory, keyed by xano_id.
// First call kicks off a single fetch; every subsequent caller awaits the same
// promise. Use `useEmployees()` in components or `getCachedEmployeeMap()` for
// ad-hoc lookups. Re-fetches naturally after a hard refresh.
import { useEffect, useState } from 'react'

let cached = null   // Promise<Map<id, employee>>

export function loadEmployees() {
  if (cached) return cached
  cached = fetch('/api/employees', { credentials: 'include' })
    .then((r) => (r.ok ? r.json() : []))
    .then((arr) => {
      const list = Array.isArray(arr) ? arr : []
      return new Map(list.map((e) => [Number(e.id), e]))
    })
    .catch(() => new Map())
  return cached
}

/**
 * React hook — returns the employee map (or null while loading).
 * Don't gate UI render on this; use empty-state in `employeeLabel` instead.
 */
export function useEmployees() {
  const [map, setMap] = useState(null)
  useEffect(() => {
    let cancelled = false
    loadEmployees().then((m) => { if (!cancelled) setMap(m) })
    return () => { cancelled = true }
  }, [])
  return map
}

/**
 * Resolve a xano_id to an employee's display label (preferring `code`).
 * Returns `'unknown'` when the id isn't in the directory.
 */
export function employeeLabel(map, id) {
  if (!id) return null            // no creator stamped → show nothing, not "unknown"
  if (!map) return '…'            // still loading
  const e = map.get(Number(id))
  if (!e) return 'unknown'
  return e.code || e.name || 'unknown'
}

/**
 * Full record from the cache; useful when you need name/email/etc. for a tooltip.
 */
export function employeeRecord(map, id) {
  if (!map || !id) return null
  return map.get(Number(id)) || null
}
